import React, { useState, useEffect } from 'react';
import './DiarioVeiculosModule.css';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

interface Veiculo {
  id: string;
  placa: string;
  marca: string;
  modelo: string;
  ano: string;
  responsavel?: string;
  ultimaChegada?: string;
  ultimaPartida?: string;
  ultimaCriacao?: string;
  diasATrabalhar: number;
  diasTrabalhados: number;
  naoTrabalhados: number;
  diasCalendario: boolean[];
  percentual: number;
  temAlerta?: boolean;
  alertas?: any[];
}

interface Motorista {
  id: string;
  nome: string;
}

interface Carreta {
  placa: string;
  marca: string;
  modelo: string;
  tipo: string;
}

interface Viagem {
  cod_horario: number;
  janela: string;
  data_viagem: string;
  numero_solicitacao: string;
  descricao?: string;
  status: number;
}

const DiarioVeiculosModule: React.FC = () => {
  const [veiculos, setVeiculos] = useState<Veiculo[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [responsavelFilter, setResponsavelFilter] = useState('');
  const [sortOption, setSortOption] = useState('menos-trabalhados');
  const [periodoFilter, setPeriodoFilter] = useState(() => {
    const hoje = new Date();
    const ano = hoje.getFullYear();
    const mes = String(hoje.getMonth() + 1).padStart(2, '0');
    return `${ano}-${mes}`;
  });
  const [loading, setLoading] = useState(false);
  const [veiculosDisponiveis, setVeiculosDisponiveis] = useState<any[]>([]);
  const [showVeiculosDisponiveisModal, setShowVeiculosDisponiveisModal] = useState(false);
  const [manifestos, setManifestos] = useState<any[]>([]);
  const [flippedCards, setFlippedCards] = useState<Set<string>>(new Set());
  const [visaoGerencial, setVisaoGerencial] = useState(false);
  const [visaoProgramacao, setVisaoProgramacao] = useState(false);
  const [showProgramarModal, setShowProgramarModal] = useState(false);
  const [veiculoSelecionado, setVeiculoSelecionado] = useState<Veiculo | null>(null);
  const [viagemSelecionada, setViagemSelecionada] = useState<number | null>(null);
  const [dataProgramacao, setDataProgramacao] = useState('');
  const [horaProgramacao, setHoraProgramacao] = useState('');
  const [motoristas, setMotoristas] = useState<Motorista[]>([]);
  const [motoristaSelecionado, setMotoristaSelecionado] = useState('');
  const [carretas, setCarretas] = useState<Carreta[]>([]);
  const [carretaSelecionada, setCarretaSelecionada] = useState('');
    const [filtroMotorista, setFiltroMotorista] = useState('');
    const [filtroCarreta, setFiltroCarreta] = useState('');
  const [dropdownMotoristaAberto, setDropdownMotoristaAberto] = useState(false);
  const [dropdownCarretaAberto, setDropdownCarretaAberto] = useState(false);
  const [viagens, setViagens] = useState<Viagem[]>([]);
  const [showViagensModal, setShowViagensModal] = useState(false);
  const [novaJanela, setNovaJanela] = useState('');
  const [novaDataViagem, setNovaDataViagem] = useState('');
  const [novoNumeroSolicitacao, setNovoNumeroSolicitacao] = useState('');
  const [novaDescricao, setNovaDescricao] = useState('');
  const [viagemEditando, setViagemEditando] = useState<number | null>(null);
  const [abaAtiva, setAbaAtiva] = useState<'criticos' | 'programados' | 'utilizando'>('criticos');
  const [programacoes, setProgramacoes] = useState<Array<{
    cod_prog?: number;
    veiculo: Veiculo;
    data: string;
    hora: string;
    motorista?: string;
    carreta?: string;
    tipoCarreta?: string;
    programadoEm: Date;
    viagemJanela?: string;
    viagemData?: string;
    viagemSolicitacao?: string;
    viagemDescricao?: string;
  }>>([]);

  // Alternar flip do card
  const toggleCardFlip = (veiculoId: string) => {
    const newFlippedCards = new Set(flippedCards);
    if (newFlippedCards.has(veiculoId)) {
      newFlippedCards.delete(veiculoId);
    } else {
      newFlippedCards.add(veiculoId);
    }
    setFlippedCards(newFlippedCards);
  };

  // Buscar programações do backend
  const buscarProgramacoes = async () => {
    try {
      const response = await fetch('/api/diario-veiculos/programacoes');
      const data = await response.json();
      
      const programacoesFormatadas = data.map((p: any) => ({
        cod_prog: p.cod_prog,
        veiculo: {
          id: p.PLACA,
          placa: p.PLACA,
          marca: p.MARCA || 'N/D',
          modelo: p.MODELO || 'N/D',
          ano: '0000',
          responsavel: p.RESPONSAVEL || 'N/D',
          diasATrabalhar: 0,
          diasTrabalhados: 0,
          naoTrabalhados: 0,
          diasCalendario: [],
          percentual: 0
        },
        data: p.DATA_PROGRAMACAO,
        hora: p.HORA_PROGRAMACAO,
        motorista: p.MOTORISTA_NOME || 'N/D',
        carreta: p.CARRETA || 'N/D',
        tipoCarreta: p.TIPO_CARRETA || 'N/D',
        programadoEm: new Date(p.CRIADO_EM),
        viagemJanela: p.VIAGEM_JANELA,
        viagemData: p.VIAGEM_DATA,
        viagemSolicitacao: p.VIAGEM_SOLICITACAO,
        viagemDescricao: p.VIAGEM_DESCRICAO
      }));
      
      setProgramacoes(programacoesFormatadas);
    } catch (error) {
      console.error('Erro ao buscar programações:', error);
    }
  };

  // Buscar motoristas disponíveis
  const buscarMotoristas = async () => {
    try {
      const response = await fetch('/api/diario-veiculos/motoristas');
      const data = await response.json();
      setMotoristas(data);
    } catch (error) {
      console.error('Erro ao buscar motoristas:', error);
    }
  };

  // Buscar carretas disponíveis
  const buscarCarretas = async () => {
    try {
      const response = await fetch('/api/diario-veiculos/carretas');
      const data = await response.json();
      setCarretas(data);
    } catch (error) {
      console.error('Erro ao buscar carretas:', error);
    }
  };

  // Buscar viagens disponíveis
  const buscarViagens = async () => {
    try {
      const response = await fetch('/api/diario-veiculos/horarios');
      const data = await response.json();
      setViagens(data);
    } catch (error) {
      console.error('Erro ao buscar viagens:', error);
    }
  };

  // Salvar nova viagem ou editar existente
  const salvarViagem = async () => {
    if (!novaJanela || !novaDataViagem || !novoNumeroSolicitacao) {
      alert('Por favor, informe a janela, data e número de solicitação');
      return;
    }

    try {
      if (viagemEditando) {
        // Editar
        await fetch(`/api/diario-veiculos/horarios/${viagemEditando}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ janela: novaJanela, data_viagem: novaDataViagem, numero_solicitacao: novoNumeroSolicitacao, descricao: novaDescricao })
        });
      } else {
        // Criar novo
        await fetch('/api/diario-veiculos/horarios', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ janela: novaJanela, data_viagem: novaDataViagem, numero_solicitacao: novoNumeroSolicitacao, descricao: novaDescricao })
        });
      }
      
      setNovaJanela('');
      setNovaDataViagem('');
      setNovoNumeroSolicitacao('');
      setNovaDescricao('');
      setViagemEditando(null);
      await buscarViagens();
    } catch (error) {
      console.error('Erro ao salvar viagem:', error);
      alert('Erro ao salvar viagem');
    }
  };

  // Deletar viagem
  const deletarViagem = async (id: number) => {
    if (!window.confirm('Deseja realmente excluir esta viagem?')) return;
    
    try {
      await fetch(`/api/diario-veiculos/horarios/${id}`, {
        method: 'DELETE'
      });
      await buscarViagens();
    } catch (error) {
      console.error('Erro ao deletar viagem:', error);
      alert('Erro ao deletar viagem');
    }
  };

  // Editar viagem
  const editarViagem = (viagem: Viagem) => {
    setNovaJanela(viagem.janela);
    setNovaDataViagem(viagem.data_viagem);
    setNovoNumeroSolicitacao(viagem.numero_solicitacao);
    setNovaDescricao(viagem.descricao || '');
    setViagemEditando(viagem.cod_horario);
  };
  // Abrir modal para programar veículo
  const abrirModalProgramacao = (veiculo: Veiculo, event: React.MouseEvent) => {
    event.stopPropagation();
    setVeiculoSelecionado(veiculo);
    setViagemSelecionada(null);
    setDataProgramacao('');
    setHoraProgramacao('');
    setMotoristaSelecionado('');
    setCarretaSelecionada('');
    setFiltroMotorista('');
    setFiltroCarreta('');
    setDropdownMotoristaAberto(false);
    setDropdownCarretaAberto(false);
    setShowProgramarModal(true);
  };

  // Salvar programação
  const salvarProgramacao = async () => {
    if (!veiculoSelecionado || !viagemSelecionada || !motoristaSelecionado || !carretaSelecionada) {
      alert('Por favor, selecione uma viagem, um motorista e uma carreta');
      return;
    }

    try {
      const response = await fetch('/api/diario-veiculos/programacoes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          placa: veiculoSelecionado.placa,
          data: dataProgramacao,
          hora: horaProgramacao,
          motorista: motoristaSelecionado,
          carreta: carretaSelecionada,
          cod_viagem: viagemSelecionada,
          observacoes: null,
          usuario: null
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erro ao salvar programação');
      }

      const result = await response.json();
      
      // Recarregar programações
      await buscarProgramacoes();
      
      setShowProgramarModal(false);
      setVeiculoSelecionado(null);
      alert('Veículo programado com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar programação:', error);
      alert('Erro ao salvar programação. Tente novamente.');
    }
  };

  // Obter todos os dias do mês
  const getDiasDoMes = (anoMes: string): number[] => {
    const [ano, mes] = anoMes.split('-').map(Number);
    const diasNoMes = new Date(ano, mes, 0).getDate();
    return Array.from({ length: diasNoMes }, (_, i) => i + 1);
  };

  // Calcular dias úteis no mês
  const getDiasUteisNoMes = (anoMes: string): number => {
    const [ano, mes] = anoMes.split('-').map(Number);
    const diasNoMes = new Date(ano, mes, 0).getDate();
    let diasUteis = 0;
    
    for (let dia = 1; dia <= diasNoMes; dia++) {
      const data = new Date(ano, mes - 1, dia);
      const diaSemana = data.getDay();
      if (diaSemana >= 1 && diaSemana <= 5) {
        diasUteis++;
      }
    }
    return diasUteis;
  };

  // Buscar veículos do backend
  const fetchVeiculos = async (): Promise<Veiculo[]> => {
    try {
      const response = await fetch('/api/diario-veiculos/veiculos');
      const text = await response.text();
      if (!response.ok) {
        console.error('Erro ao buscar veículos:', response.status, response.statusText);
        return [];
      }
      const data = JSON.parse(text);
      return data.map((item: any) => ({
        id: item.PLACA || String(Math.random()),
        placa: item.PLACA || 'SEM PLACA',
        marca: item.MARCA || 'N/D',
        modelo: item.MODELO || 'N/D',
        ano: item.ANO || '0000',
        responsavel: item.RESPONSAVEL || 'N/D',
        ultimaChegada: item.ULTIMA_CHEGADA,
        ultimaPartida: item.ULTIMA_PARTIDA,
        ultimaCriacao: item.ULTIMA_CRIACAO,
        diasATrabalhar: 0,
        diasTrabalhados: 0,
        naoTrabalhados: 0,
        diasCalendario: [],
        percentual: 0
      }));
    } catch (error) {
      console.error('Erro ao buscar veículos:', error);
      return [];
    }
  };

  // Buscar dias trabalhados
  const fetchDiasTrabalhados = async (placas: string[], dataInicio: string, dataFim: string) => {
    if (placas.length === 0) return [];
    const params = new URLSearchParams();
    params.append('dataInicio', dataInicio);
    params.append('dataFim', dataFim);
    placas.forEach(placa => params.append('placas', placa));
    
    const response = await fetch(`/api/diario-veiculos/dias-trabalhados?${params.toString()}`);
    return await response.json();
  };

  // Buscar manifestos
  const fetchManifestos = async (placas: string[], dataInicio: string, dataFim: string) => {
    if (placas.length === 0) return [];
    const params = new URLSearchParams();
    params.append('dataInicio', dataInicio);
    params.append('dataFim', dataFim);
    placas.forEach(placa => params.append('placas', placa));
    const response = await fetch(`/api/diario-veiculos/manifestos?${params.toString()}`);
    return await response.json();
  };

  // Buscar veículos disponíveis
  const fetchVeiculosDisponiveis = async (dataInicio: string, dataFim: string) => {
    const params = new URLSearchParams();
    params.append('dataInicio', dataInicio);
    params.append('dataFim', dataFim);
    const response = await fetch(`/api/diario-veiculos/veiculos-disponiveis?${params.toString()}`);
    return await response.json();
  };

  // Calcular dias trabalhados reais do veículo
  const calcularDiasTrabalhados = (veiculo: Veiculo, diasTrabalhadosData: any[], periodo: string): Veiculo => {
    const diasDoMes = getDiasDoMes(periodo);
    const diasUteisNoMes = getDiasUteisNoMes(periodo);
    
    const dadosVeiculo = diasTrabalhadosData.find(d => String(d.placa) === veiculo.id);
    
    if (!dadosVeiculo) {
      return {
        ...veiculo,
        diasATrabalhar: diasUteisNoMes,
        diasTrabalhados: 0,
        naoTrabalhados: diasUteisNoMes,
        percentual: 0,
        diasCalendario: new Array(diasDoMes.length).fill(false)
      };
    }

    const diasCalendario = diasDoMes.map(dia => {
      const diaFormatado = `${periodo}-${String(dia).padStart(2, '0')}`;
      return dadosVeiculo.diasTrabalhados.includes(diaFormatado);
    });

    let diasTrabalhadosTotal = 0;
    let diasTrabalhadosUteis = 0;
    let naoTrabalhadosUteis = 0;
    
    diasDoMes.forEach((dia, idx) => {
      const data = new Date(Number(periodo.split('-')[0]), Number(periodo.split('-')[1]) - 1, dia);
      const diaSemana = data.getDay();
      const trabalhouNoDia = diasCalendario[idx];
      
      if (trabalhouNoDia) {
        diasTrabalhadosTotal++;
      }
      
      if (diaSemana >= 1 && diaSemana <= 5) {
        if (trabalhouNoDia) {
          diasTrabalhadosUteis++;
        } else {
          naoTrabalhadosUteis++;
        }
      }
    });

    // Calcular dias parados desde a última chegada
    let diasParados = 0;
    if (veiculo.ultimaChegada && veiculo.ultimaChegada !== '0000-00-00 00:00:00') {
      const dataUltimaChegada = new Date(veiculo.ultimaChegada);
      const hoje = new Date();
      const diferencaMs = hoje.getTime() - dataUltimaChegada.getTime();
      diasParados = Math.floor(diferencaMs / (1000 * 60 * 60 * 24));
      if (diasParados < 0) diasParados = 0;
    }

    const percentual = diasUteisNoMes > 0 ? Math.round((diasTrabalhadosUteis / diasUteisNoMes) * 100) : 0;

    return {
      ...veiculo,
      diasATrabalhar: diasUteisNoMes,
      diasTrabalhados: diasTrabalhadosTotal,
      naoTrabalhados: diasParados,
      percentual,
      diasCalendario,
      temAlerta: dadosVeiculo.temAlerta || false,
      alertas: dadosVeiculo.alertas || []
    };
  };

  const buscarDados = async () => {
    setLoading(true);
    const veiculosApi = await fetchVeiculos();
    const [ano, mes] = periodoFilter.split('-').map(Number);
    const dataInicio = `${ano}-${String(mes).padStart(2, '0')}-01`;
    const dataFim = `${ano}-${String(mes).padStart(2, '0')}-${getDiasDoMes(periodoFilter).length}`;
    const placas = veiculosApi.map(v => v.id);
    
    const [diasTrabalhadosData, manifestosData, veiculosDisponiveisData] = await Promise.all([
      fetchDiasTrabalhados(placas, dataInicio, dataFim),
      fetchManifestos(placas, dataInicio, dataFim),
      fetchVeiculosDisponiveis(dataInicio, dataFim)
    ]);

    const veiculosComDados = veiculosApi.map(v => calcularDiasTrabalhados(v, diasTrabalhadosData, periodoFilter));
    
    setVeiculos(veiculosComDados);
    setManifestos(manifestosData);
    setVeiculosDisponiveis(veiculosDisponiveisData);
    setLoading(false);
  };

  useEffect(() => {
    buscarDados();
    buscarProgramacoes();
    buscarMotoristas();
    buscarCarretas();
    buscarViagens();
  }, [periodoFilter]);

  // Fechar dropdowns ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.dropdown-motorista') && !target.closest('.dropdown-carreta')) {
        setDropdownMotoristaAberto(false);
        setDropdownCarretaAberto(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filtros e ordenação
  const veiculosFiltrados = veiculos
    .filter(v => 
      v.placa.toLowerCase().includes(searchTerm.toLowerCase()) &&
      (responsavelFilter === '' || v.responsavel === responsavelFilter)
    )
    .sort((a, b) => {
      if (sortOption === 'menos-trabalhados') {
        return a.diasTrabalhados - b.diasTrabalhados;
      } else if (sortOption === 'mais-trabalhados') {
        return b.diasTrabalhados - a.diasTrabalhados;
      } else if (sortOption === 'percentual-crescente') {
        return a.percentual - b.percentual;
      } else if (sortOption === 'percentual-decrescente') {
        return b.percentual - a.percentual;
      }
      return 0;
    });

  // Estatísticas
  const total = veiculos.length;
  const totalAtivos = veiculosFiltrados.length;
  const diasUteisNoMes = getDiasUteisNoMes(periodoFilter);

  // Veículos disponíveis filtrados conforme os filtros aplicados
  const veiculosDisponiveisFiltrados = veiculosDisponiveis.filter(v => {
    const placaMatch = v.PLACA && v.PLACA.toLowerCase().includes(searchTerm.toLowerCase());
    const responsavelMatch = responsavelFilter === '' || v.RESPONSAVEL === responsavelFilter;
    return placaMatch && responsavelMatch;
  });

  // Exportar PDF
  const exportarPDF = () => {
    const doc = new jsPDF();
    doc.text('Relatório de Diário de Veículos', 14, 15);
    doc.text(`Período: ${periodoFilter}`, 14, 22);
    
    const tableData = veiculosFiltrados.map(v => [
      v.placa,
      `${v.marca} ${v.modelo}`,
      v.ano,
      v.diasTrabalhados.toString(),
      v.diasATrabalhar.toString(),
      `${v.percentual}%`
    ]);

    autoTable(doc, {
      head: [['Placa', 'Veículo', 'Ano', 'Dias Trab.', 'Dias Úteis', 'Percentual']],
      body: tableData,
      startY: 30
    });

    doc.save(`relatorio-veiculos-${periodoFilter}.pdf`);
  };

  // Exportar Excel
  const exportarExcel = () => {
    const data = veiculosFiltrados.map(v => ({
      'Placa': v.placa,
      'Marca': v.marca,
      'Modelo': v.modelo,
      'Ano': v.ano,
      'Dias Trabalhados': v.diasTrabalhados,
      'Dias Úteis': v.diasATrabalhar,
      'Não Trabalhados': v.naoTrabalhados,
      'Percentual': `${v.percentual}%`
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Veículos');
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const dataBlob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(dataBlob, `relatorio-veiculos-${periodoFilter}.xlsx`);
  };

  return (
    <div className="diario-relatorio">
      {/* Header */}
      <div className="header-section">
        <div className="title-area">
          <h1>🚛 Diário de Veículos</h1>
          <p>Acompanhamento de atividades dos cavalos mecânicos</p>
        </div>
        <div className="actions-area">
          <button 
            className="btn-print" 
            onClick={() => {
              setVisaoGerencial(!visaoGerencial);
            }}
            style={{background: visaoGerencial ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' : 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)'}}
          >
            {visaoGerencial ? 'Visão Operacional' : 'Visão Gerencial'}
          </button>
          <button className="btn-print" onClick={exportarPDF}>📄 PDF</button>
          <button className="btn-print" style={{background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'}} onClick={exportarExcel}>
            📊 Excel
          </button>
          <button 
            className="btn-print" 
            onClick={() => setShowViagensModal(true)}
            style={{background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'}}
          >
            🚚 Gerenciar Viagens
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="filters-section">
        <div className="filters-row">
          <input
            type="text"
            placeholder="🔍 Buscar por placa..."
            className="search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          
          <select 
            className="search-input" 
            value={responsavelFilter} 
            onChange={(e) => setResponsavelFilter(e.target.value)}
          >
            <option value="">👤 Todos os Responsáveis</option>
            {Array.from(new Set(veiculos.map(v => v.responsavel).filter(Boolean))).sort().map(resp => (
              <option key={resp} value={resp}>{resp}</option>
            ))}
          </select>
          
          <input
            type="month"
            className="periodo-input"
            value={periodoFilter}
            onChange={(e) => setPeriodoFilter(e.target.value)}
          />
          
          <select className="order-select" value={sortOption} onChange={(e) => setSortOption(e.target.value)}>
            <option value="menos-trabalhados">⬆️ Menos Trabalhados</option>
            <option value="mais-trabalhados">⬇️ Mais Trabalhados</option>
            <option value="percentual-crescente">📈 % Crescente</option>
            <option value="percentual-decrescente">📉 % Decrescente</option>
          </select>
        </div>
      </div>

      {/* Estatísticas */}
      <div className="stats-section">
        <div className="stats-cards">
          <div className="stat-card">
            <div className="stat-number">{total}</div>
            <div className="stat-label">Total Veículos</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{totalAtivos}</div>
            <div className="stat-label">Veículos Filtrados</div>
          </div>
          <div className="stat-card" onClick={() => setShowVeiculosDisponiveisModal(true)} style={{cursor: 'pointer'}}>
            <div className="stat-number">{veiculosDisponiveisFiltrados.length}</div>
            <div className="stat-label">Veículos Disponíveis</div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="content-section">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Carregando dados dos veículos...</p>
          </div>
        ) : visaoProgramacao ? (
          // VISÃO DE PROGRAMAÇÃO
          <div className="visao-programacao">
            <div style={{marginBottom: 20, padding: 20, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0'}}>
              <h2 style={{margin: 0, marginBottom: 10, color: '#1e293b'}}>🗓️ Programação de Veículos</h2>
              <p style={{margin: 0, color: '#64748b'}}>Veículos programados para manifestos futuros</p>
            </div>

            {programacoes.length === 0 ? (
              <div style={{
                padding: 40,
                textAlign: 'center',
                background: '#f8fafc',
                borderRadius: 8,
                border: '2px dashed #cbd5e1'
              }}>
                <div style={{fontSize: '3em', marginBottom: 10}}>📋</div>
                <h3 style={{color: '#64748b', margin: 0}}>Nenhuma programação cadastrada</h3>
                <p style={{color: '#94a3b8', marginTop: 10}}>
                  Acesse a Visão Gerencial e clique nos veículos para programá-los
                </p>
              </div>
            ) : (
              <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 20}}>
                {programacoes
                  .sort((a, b) => new Date(a.data + ' ' + a.hora).getTime() - new Date(b.data + ' ' + b.hora).getTime())
                  .map((prog, idx) => (
                    <div key={idx} style={{
                      maxWidth: 340,
                      background: '#ffffff',
                      padding: 0,
                      border: '2px solid #F37216',
                      borderRadius: 12,
                      boxShadow: '0 4px 12px rgba(236, 72, 153, 0.15)',
                      overflow: 'hidden',
                      transition: 'transform 0.2s, box-shadow 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 8px 20px rgba(236, 72, 153, 0.25)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(236, 72, 153, 0.15)';
                    }}>
                    {/* Header com placa e botão cancelar */}
                    <div style={{
                      background: 'linear-gradient(135deg, #F37216 0%, #F37216 100%)',
                      padding: '10px 10px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
                        <span style={{fontSize: '1.5em'}}>🚛</span>
                        <strong style={{fontSize: '1.3em', color: '#ffffff', letterSpacing: '0.5px'}}>
                          {prog.veiculo.placa}
                        </strong>
                      </div>
                      <button
                        onClick={async () => {
                          if (prog.cod_prog) {
                            if (!window.confirm('Deseja realmente cancelar esta programação?')) return;
                            try {
                              const response = await fetch(`/api/diario-veiculos/programacoes/${prog.cod_prog}`, {
                                method: 'DELETE'
                              });
                              if (response.ok) {
                                await buscarProgramacoes();
                                alert('Programação cancelada com sucesso!');
                              }
                            } catch (error) {
                              console.error('Erro ao cancelar programação:', error);
                              alert('Erro ao cancelar programação');
                            }
                          }
                        }}
                        style={{
                          background: 'red',
                          color: '#ffffff',
                          border: '1px solid rgba(255, 255, 255, 0.3)',
                          padding: '10px 10px',
                          borderRadius: 6,
                          width: '90px',
                          cursor: 'pointer',
                          fontSize: '0.75em',
                          fontWeight: '600',
                          transition: 'all 0.2s',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center',
                          gap: 4
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#ffffff';
                          e.currentTarget.style.color = '#dc2626';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'red';
                          e.currentTarget.style.color = '#ffffff';
                        }}
                      >
                        Excluir
                      </button>
                    </div>
                    
                    {/* Corpo do card */}
                    <div style={{padding: 20}}>
                      {/* Informações do veículo */}
                      <div style={{
                        background: 'linear-gradient(135deg, #fdf2f8 0%, #fce7f3 100%)',
                        padding: 16,
                        borderRadius: 10,
                        marginBottom: 16,
                        border: '1px solid #fbcfe8'
                      }}>
                        <div style={{fontSize: '0.75em', color: '#9ca3af', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600'}}>
                          Informações do Veículo
                        </div>
                        <div style={{display: 'flex', flexDirection: 'column', gap: 6}}>
                          <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                            <span style={{color: '#ec4899', fontSize: '0.9em'}}>🚗</span>
                            <span style={{color: '#1e293b', fontSize: '0.95em'}}>
                            {prog.veiculo.marca} <strong>{prog.veiculo.modelo}</strong>                                                    
                            </span>
                          </div>
                          <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                            <span style={{color: '#ec4899', fontSize: '0.9em'}}>👤</span>
                            <span style={{color: '#64748b', fontSize: '0.9em'}}>
                              <strong>{prog.veiculo.responsavel || 'N/D'}</strong>
                            </span>
                          </div>
                          <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                            <span style={{color: '#ec4899', fontSize: '0.9em'}}>🧑‍✈️</span>
                            <span style={{color: '#1e293b', fontSize: '0.95em', fontWeight: 'bold'}}>
                              {prog.motorista || 'N/D'}
                            </span>
                          </div>
                          <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                            <span style={{color: '#ec4899', fontSize: '0.9em'}}>🚛</span>
                            <span style={{color: '#1e293b', fontSize: '0.95em', fontWeight: 'bold'}}>
                              {prog.carreta || 'N/D'} {prog.tipoCarreta ? `(${prog.tipoCarreta})` : ''}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Data e hora programada */}
                      <div style={{
                        background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)',
                        padding: 18,
                        borderRadius: 10,
                        marginBottom: 16,
                        border: '1px solid #fed7aa',
                        textAlign: 'center'
                      }}>
                        <div style={{fontSize: '0.75em', color: '#9ca3af', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600'}}>
                          Programado para:
                        </div>
                        <div style={{
                          fontSize: '1.0em',
                          fontWeight: 'bold',
                          color: '#c2410c',
                          marginBottom: 4,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 10
                        }}>
                          📅 {new Date(prog.data).toLocaleDateString('pt-BR')}
                        </div>
                        <div style={{
                          fontSize: '0.9em',
                          fontWeight: '600',
                          color: '#ea580c',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 6
                        }}>
                          🕐 {prog.hora}
                        </div>
                      </div>

                      {/* Footer com data de criação */}
                      <div style={{
                        fontSize: '0.75em',
                        color: '#94a3b8',
                        borderTop: '1px solid #e2e8f0',
                        paddingTop: 12,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        justifyContent: 'center'
                      }}>
                        <span>⏱️</span>
                        <span>Programado em: {prog.programadoEm.toLocaleString('pt-BR')}</span>                        
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : visaoGerencial ? (
          // VISÃO GERENCIAL
          <div className="visao-gerencial">
            <div style={{marginBottom: 20, padding: 20, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0'}}>
              <h2 style={{margin: 0, marginBottom: 10, color: '#1e293b', textAlign: 'center'}}>📊 Análise Gerencial de Performance</h2>
            </div>

            {/* Abas de categorização */}
            <div style={{display: 'flex', gap: 10, marginBottom: 20, justifyContent: 'center'}}>
              <button
                onClick={() => setAbaAtiva('criticos')}
                style={{
                  padding: '12px 24px',
                  borderRadius: 8,
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '1em',
                  transition: 'all 0.3s',
                  background: abaAtiva === 'criticos' ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' : '#e2e8f0',
                  color: abaAtiva === 'criticos' ? 'white' : '#64748b',
                  boxShadow: abaAtiva === 'criticos' ? '0 4px 6px rgba(239, 68, 68, 0.3)' : 'none'
                }}
              >
                🚨 Críticos ({veiculosFiltrados.filter(v => {
                  const programacao = programacoes.find(p => p.veiculo.placa === v.placa);
                  const manifestosAtivos = manifestos.filter(m => m.PLACA_VEICULO === v.placa && (!m.CHEGADA || m.CHEGADA === null));
                  return v.percentual <= 30 && !programacao && manifestosAtivos.length === 0;
                }).length})
              </button>
              <button
                onClick={() => setAbaAtiva('programados')}
                style={{
                  padding: '12px 24px',
                  borderRadius: 8,
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '1em',
                  transition: 'all 0.3s',
                  background: abaAtiva === 'programados' ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : '#e2e8f0',
                  color: abaAtiva === 'programados' ? 'white' : '#64748b',
                  boxShadow: abaAtiva === 'programados' ? '0 4px 6px rgba(16, 185, 129, 0.3)' : 'none'
                }}
              >
                ✅ Programados ({veiculosFiltrados.filter(v => {
                  const programacao = programacoes.find(p => p.veiculo.placa === v.placa);
                  return v.percentual <= 30 && !!programacao;
                }).length})
              </button>
              <button
                onClick={() => setAbaAtiva('utilizando')}
                style={{
                  padding: '12px 24px',
                  borderRadius: 8,
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '1em',
                  transition: 'all 0.3s',
                  background: abaAtiva === 'utilizando' ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' : '#e2e8f0',
                  color: abaAtiva === 'utilizando' ? 'white' : '#64748b',
                  boxShadow: abaAtiva === 'utilizando' ? '0 4px 6px rgba(245, 158, 11, 0.3)' : 'none'
                }}
              >
                🚚 Em Viagem ({veiculosFiltrados.filter(v => {
                  const manifestosAtivos = manifestos.filter(m => m.PLACA_VEICULO === v.placa && (!m.CHEGADA || m.CHEGADA === null));
                  return v.percentual <= 30 && manifestosAtivos.length > 0;
                }).length})
              </button>
            </div>

            {/* Aba Críticos */}
            {abaAtiva === 'criticos' && (
              <div style={{marginBottom: 30}}>                
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 15}}>
                  {veiculosFiltrados.filter(v => {
                    const programacao = programacoes.find(p => p.veiculo.placa === v.placa);
                    const manifestosAtivos = manifestos.filter(m => m.PLACA_VEICULO === v.placa && (!m.CHEGADA || m.CHEGADA === null));
                    return v.percentual <= 30 && !programacao && manifestosAtivos.length === 0;
                  }).map(veiculo => {
                    const dataUltimoManifesto = veiculo.ultimaCriacao && veiculo.ultimaCriacao !== '0000-00-00 00:00:00'
                      ? new Date(veiculo.ultimaCriacao).toLocaleDateString('pt-BR')
                      : 'Sem manifestos';
                    
                    return (
                    <div 
                      key={veiculo.id} 
                      style={{
                        padding: 15,
                        background: '#fff',
                        border: '2px solid #ef4444',
                        borderRadius: 8,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        cursor: 'pointer',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        position: 'relative'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-4px)';
                        e.currentTarget.style.boxShadow = '0 6px 12px rgba(0,0,0,0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                      }}
                      onClick={(e) => abrirModalProgramacao(veiculo, e)}
                    >
                      
                      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10}}>
                        <strong style={{fontSize: '1.1em', color: '#1e293b'}}>🚛 {veiculo.placa}</strong>
                      </div>
                      <div style={{fontSize: '0.9em', color: '#64748b', marginBottom: 8}}>
                        <div><strong>Marca/Modelo:</strong> {veiculo.marca} {veiculo.modelo}</div>
                        <div><strong>Responsável:</strong> {veiculo.responsavel || 'N/D'}</div>
                        <div><strong>Último Manifesto:</strong> {dataUltimoManifesto}</div>
                        <div><strong>Data de Chegada:</strong> {veiculo.ultimaChegada && veiculo.ultimaChegada !== '0000-00-00 00:00:00' ? new Date(veiculo.ultimaChegada).toLocaleDateString('pt-BR') : '-'}</div>
                      </div>
                      <div style={{
                        padding: 10,
                        background: '#fef2f2',
                        borderRadius: 6,
                        marginTop: 10
                      }}>
                        <div style={{fontSize: '0.85em', marginBottom: 5}}>
                          <strong>Dias trabalhados:</strong> {veiculo.diasTrabalhados} de {veiculo.diasATrabalhar} dias úteis
                        </div>
                        <div style={{fontSize: '0.85em', color: '#dc2626'}}>
                          <strong>Dias parados:</strong> {veiculo.naoTrabalhados} dias úteis
                        </div>
                      </div>
                      <div style={{
                        marginTop: 10,
                        padding: 8,
                        background: '#fff7ed',
                        borderRadius: 6,
                        fontSize: '0.85em',
                        color: '#9a3412',
                        textAlign: 'center'
                      }}>
                        👆Clique para programar👆
                      </div>
                    </div>
                  );
                  })}
                </div>
              </div>
            )}

            {/* Aba Programados */}
            {abaAtiva === 'programados' && (
              <div style={{marginBottom: 30}}>
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 15}}>
                  {veiculosFiltrados.filter(v => {
                    const programacao = programacoes.find(p => p.veiculo.placa === v.placa);
                    return v.percentual <= 30 && !!programacao;
                  }).map(veiculo => {
                    const programacao = programacoes.find(p => p.veiculo.placa === veiculo.placa);
                    
                    return (
                    <div 
                      key={veiculo.id} 
                      style={{
                        padding: 15,
                        background: '#fff',
                        border: '2px solid #10b981',
                        borderRadius: 8,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        cursor: 'default',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        position: 'relative',
                        opacity: 0.95
                      }}
                    >
                      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10}}>
                        <strong style={{fontSize: '1.1em', color: '#1e293b'}}>🚛 {veiculo.placa}</strong>
                        <span style={{
                          background: '#d1fae5',
                          color: '#065f46',
                          padding: '4px 12px',
                          borderRadius: 20,
                          fontSize: '0.85em',
                          fontWeight: 'bold'
                        }}>✓ PROGRAMADO</span>
                      </div>
                      <div style={{fontSize: '0.9em', color: '#64748b', marginBottom: 8}}>
                        <div><strong>Marca/Modelo:</strong> {veiculo.marca} {veiculo.modelo}</div>
                        <div><strong>Responsável:</strong> {veiculo.responsavel || 'N/D'}</div>
                        <div style={{marginTop: 10, padding: 10, background: '#ecfdf5', borderRadius: 6, border: '1px solid #a7f3d0'}}>
                          <div><strong>Solicitação:</strong> {programacao?.viagemSolicitacao || '-'}</div>
                          <div><strong>Data viagem:</strong> {programacao?.viagemData ? new Date(programacao.viagemData).toLocaleDateString('pt-BR') : '-'}</div>
                          <div><strong>Horário:</strong> {programacao?.hora || '-'}</div>
                          <div><strong>Motorista:</strong> {programacao?.motorista || '-'}</div>
                          <div><strong>Carreta:</strong> {programacao?.carreta || '-'}</div> 
                        </div>
                      </div>
                      <div style={{
                        padding: 10,
                        background: '#fef2f2',
                        borderRadius: 6,
                        marginTop: 10
                      }}>
                        <div style={{fontSize: '0.85em', marginBottom: 5}}>
                          <strong>Dias trabalhados:</strong> {veiculo.diasTrabalhados} de {veiculo.diasATrabalhar} dias úteis
                        </div>
                        <div style={{fontSize: '0.85em', color: '#dc2626'}}>
                          <strong>Dias parados:</strong> {veiculo.naoTrabalhados} dias úteis
                        </div>
                      </div>
                    </div>
                  );
                  })}
                </div>
              </div>
            )}

            {/* Aba Utilizando */}
            {abaAtiva === 'utilizando' && (
              <div style={{marginBottom: 30}}>
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 15}}>
                  {veiculosFiltrados.filter(v => {
                    const manifestosAtivos = manifestos.filter(m => m.PLACA_VEICULO === v.placa && (!m.CHEGADA || m.CHEGADA === null));
                    return v.percentual <= 30 && manifestosAtivos.length > 0;
                  }).map(veiculo => {
                    const manifestosAtivos = manifestos.filter(m => m.PLACA_VEICULO === veiculo.placa && (!m.CHEGADA || m.CHEGADA === null));
                    
                    return (
                    <div 
                      key={veiculo.id} 
                      style={{
                        padding: 15,
                        background: '#fff',
                        border: '2px solid #f59e0b',
                        borderRadius: 8,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        cursor: 'pointer',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        position: 'relative'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-4px)';
                        e.currentTarget.style.boxShadow = '0 6px 12px rgba(0,0,0,0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                      }}
                      onClick={(e) => abrirModalProgramacao(veiculo, e)}
                    >
                      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10}}>
                        <strong style={{fontSize: '1.1em', color: '#1e293b'}}>🚛 {veiculo.placa}</strong>
                        <span style={{
                          background: '#fef3c7',
                          color: '#92400e',
                          padding: '4px 12px',
                          borderRadius: 20,
                          fontSize: '0.85em',
                          fontWeight: 'bold'
                        }}>🚚 EM USO</span>
                      </div>
                      <div style={{fontSize: '0.9em', color: '#64748b', marginBottom: 8}}>
                        <div><strong>Marca/Modelo:</strong> {veiculo.marca} {veiculo.modelo}</div>
                        <div><strong>Responsável:</strong> {veiculo.responsavel || 'N/D'}</div>
                        <div style={{marginTop: 10, padding: 10, background: '#fffbeb', borderRadius: 6, border: '1px solid #fde68a'}}>
                          <div style={{fontWeight: 'bold', marginBottom: 8, color: '#92400e'}}>📋 Manifestos Ativos ({manifestosAtivos.length})</div>
                          {manifestosAtivos.slice(0, 2).map((m, idx) => (
                            <div key={idx} style={{fontSize: '0.85em', marginBottom: 6, paddingBottom: 6, borderBottom: idx < 1 && manifestosAtivos.length > 1 ? '1px solid #fde68a' : 'none'}}>
                              <div><strong>Manifesto:</strong> #{m.NUMERO}</div>
                              <div><strong>Partida:</strong> {m.PARTIDA ? new Date(m.PARTIDA).toLocaleDateString('pt-BR') : 'N/D'}</div>
                              <div><strong>Destino:</strong> {m.DESTINO_CIDADE}/{m.DESTINO_UF}</div>
                            </div>
                          ))}
                          {manifestosAtivos.length > 2 && (
                            <div style={{fontSize: '0.8em', color: '#92400e', fontStyle: 'italic', marginTop: 5}}>
                              +{manifestosAtivos.length - 2} manifesto(s) ativo(s)
                            </div>
                          )}
                        </div>
                      </div>
                      <div style={{
                        padding: 10,
                        background: '#fef2f2',
                        borderRadius: 6,
                        marginTop: 10
                      }}>
                        <div style={{fontSize: '0.85em', marginBottom: 5}}>
                          <strong>Dias trabalhados:</strong> {veiculo.diasTrabalhados} de {veiculo.diasATrabalhar} dias úteis
                        </div>
                        <div style={{fontSize: '0.85em', color: '#dc2626'}}>
                          <strong>Dias parados:</strong> {veiculo.naoTrabalhados} dias úteis
                        </div>
                      </div>
                      <div style={{
                        marginTop: 10,
                        padding: 8,
                        background: '#fef3c7',
                        borderRadius: 6,
                        fontSize: '0.85em',
                        color: '#92400e',
                        textAlign: 'center'
                      }}>
                        💡 Veículo em uso - Clique para programar próxima viagem
                      </div>
                    </div>
                  );
                  })}
                </div>
              </div>
            )}
          </div>
        ) : (
          // VISÃO OPERACIONAL (ORIGINAL)
          <div className="drivers-section">
            <div className="drivers-grid">
              {veiculosFiltrados.map(veiculo => {
                const veiculoManifestos = manifestos.filter(m => m.PLACA_VEICULO === veiculo.placa);
                const isFlipped = flippedCards.has(veiculo.id);
                
                return (
                  <div
                    key={veiculo.id}
                    className={`driver-card ${isFlipped ? 'flipped' : ''}`}
                    onClick={() => toggleCardFlip(veiculo.id)}
                  >
                    <div className="card-inner">
                      {/* Frente do card */}
                      <div className="card-front">
                        <div className="driver-header">
                          <h3 className="driver-name">🚛 {veiculo.placa}</h3>
                          <div className="driver-percentage">{veiculo.percentual}%</div>
                        </div>
                        
                        {/* Alertas de manifestos com problemas */}
                        {veiculo.temAlerta && veiculo.alertas && veiculo.alertas.length > 0 && (
                          <div style={{
                            background: '#fee2e2', 
                            border: '2px solid #ef4444', 
                            borderRadius: '8px', 
                            padding: '10px', 
                            marginBottom: '10px'
                          }}>
                            <div style={{fontWeight: 'bold', color: '#dc2626', marginBottom: '5px'}}>
                              ⚠️ Alertas de Manifestos
                            </div>
                            {veiculo.alertas.map((alerta: any, idx: number) => (
                              <div key={idx} style={{
                                fontSize: '0.85em', 
                                color: '#991b1b', 
                                marginTop: '3px',
                                lineHeight: '1.4'
                              }}>
                                {alerta.mensagem}
                              </div>
                            ))}
                          </div>
                        )}
                        
                        <div className="driver-stats">
                          <div><strong>Marca:</strong> {veiculo.marca}</div>
                          <div><strong>Modelo:</strong> {veiculo.modelo}</div>
                          <div><strong>Responsável:</strong> {veiculo.responsavel || 'N/D'}</div>
                          <div style={{marginTop: 10}}>
                            <strong>Dias trabalhados:</strong> {veiculo.diasTrabalhados} / {veiculo.diasATrabalhar} dias úteis
                          </div>
                        </div>

                        <div className="progress-bar">
                          <div className="progress-fill" style={{width: `${veiculo.percentual}%`}}></div>
                        </div>

                        <div className="calendar-grid">
                          {getDiasDoMes(periodoFilter).map((dia, idx) => {
                            const trabalhou = veiculo.diasCalendario[idx];
                            const data = new Date(Number(periodoFilter.split('-')[0]), Number(periodoFilter.split('-')[1]) - 1, dia);
                            const diaSemana = data.getDay();
                            const diaSemanaNome = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'][diaSemana];
                            return (
                              <div
                                key={dia}
                                className={`day-circle ${trabalhou ? 'worked' : 'not-worked'}`}
                                title={`${diaSemanaNome} ${dia} - ${trabalhou ? 'Trabalhou' : 'Não trabalhou'}`}
                              >
                                <div style={{fontSize: '0.75em', fontWeight: 500, color: '#fff'}}>{diaSemanaNome}</div>
                                <div>{dia}</div>
                              </div>
                            );
                          })}
                        </div>

                        <div className="legend">
                          <div className="legend-item">
                            <div className="legend-dot worked"></div>
                            <span>Trabalhou</span>
                          </div>
                          <div className="legend-item">
                            <div className="legend-dot not-worked"></div>
                            <span>Não trabalhou</span>
                          </div>
                        </div>

                        <div className="card-flip-hint">👆 Clique para ver manifestos</div>
                      </div>

                      {/* Verso do card */}
                      <div className="card-back">
                        <div className="driver-header">
                          <h3 className="driver-name">📋 Manifestos</h3>
                          <div className="manifesto-count">{veiculoManifestos.length}</div>
                        </div>

                        {veiculoManifestos.length > 0 ? (
                          <div className="manifestos-list">
                            {veiculoManifestos.map((m, idx) => (
                              <div key={idx} className="manifesto-item">
                                <div className="manifesto-numero">Manifesto #{m.NUMERO}</div>
                                <div className="manifesto-datas">
                                  <div>🚀 Partida: {m.PARTIDA ? new Date(m.PARTIDA).toLocaleString('pt-BR') : 'N/D'}</div>
                                  <div>🏁 Chegada: {m.CHEGADA ? new Date(m.CHEGADA).toLocaleString('pt-BR') : 'Em andamento'}</div>
                                  <div>📍 Origem: {m.ORIGEM_CIDADE}/{m.ORIGEM_UF}</div>
                                  <div>📍 Destino: {m.DESTINO_CIDADE}/{m.DESTINO_UF}</div>
                                </div>
                                <div className={`manifesto-status status-${m.STATUS.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')}`}>
                                  {m.STATUS}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="no-manifestos">Nenhum manifesto encontrado</div>
                        )}

                        <div className="card-flip-hint">👆 Clique para voltar</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Modal de veículos disponíveis */}
      {showVeiculosDisponiveisModal && (
        <div className="modal-overlay" onClick={() => setShowVeiculosDisponiveisModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Veículos Disponíveis</h2>
            <div style={{maxHeight: '60vh', overflowY: 'auto'}}>
              {veiculosDisponiveisFiltrados.map((v, idx) => (
                <div key={idx} style={{padding: '10px', borderBottom: '1px solid #eee'}}>
                  <strong>{v.PLACA}</strong> - Última chegada: {new Date(v.ULTIMA_CHEGADA).toLocaleString('pt-BR')}
                </div>
              ))}
            </div>
            <button onClick={() => setShowVeiculosDisponiveisModal(false)} style={{marginTop: 20}}>Fechar</button>
          </div>
        </div>
      )}

      {/* Modal de Programação de Veículo */}
      {showProgramarModal && veiculoSelecionado && (
        <div className="modal-overlay" onClick={() => setShowProgramarModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{maxWidth: 650, width: '99%'}}>
            <h2 style={{margin: 0, marginBottom: 20, color: '#1e293b', textAlign: 'center'}}>🗓️ Programar Veículo</h2>
            <div style={{
              background: '#f8fafc',
              padding: 15,
              borderRadius: 8,
              marginBottom: 20,
              border: '1px solid #e2e8f0'
            }}>
              <div style={{fontSize: '1.1em', fontWeight: 'bold', color: '#1e293b', marginBottom: 10}}>
                🚛 {veiculoSelecionado.placa}
              </div>
              <div style={{fontSize: '0.9em', color: '#64748b'}}>
                <div><strong>Marca/Modelo:</strong> {veiculoSelecionado.marca} {veiculoSelecionado.modelo}</div>
                <div>
                  <strong>Ultimo Manifesto:</strong> {
                    veiculoSelecionado.ultimaChegada
                      ? new Date(veiculoSelecionado.ultimaChegada).toLocaleString('pt-BR')
                      : '-'
                  }
                </div>                 
                <div><strong>Responsável:</strong> {veiculoSelecionado.responsavel || 'N/D'}</div>
              </div>
            </div>

            <div style={{marginBottom: 20}}>
              <label style={{display: 'block', marginBottom: 8, fontWeight: 'bold', color: '#1e293b'}}>
                � Viagem: <span style={{color: '#dc2626'}}>*</span>
              </label>
              <select
                value={viagemSelecionada || ''}
                onChange={(e) => {
                  const codViagem = Number(e.target.value);
                  setViagemSelecionada(codViagem);
                  const viagem = viagens.find(v => v.cod_horario === codViagem);
                  if (viagem) {
                    setDataProgramacao(viagem.data_viagem);
                    setHoraProgramacao(viagem.janela);
                  }
                }}
                style={{
                  width: '100%',
                  padding: 10,
                  borderRadius: 6,
                  border: '1px solid #cbd5e1',
                  fontSize: '1em',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
              >
                <option value="">Selecione uma viagem</option>
                {viagens
                  .filter(v => !programacoes.some(p => p.viagemSolicitacao === v.numero_solicitacao))
                  .map((v) => (
                    <option key={v.cod_horario} value={v.cod_horario}>
                      {v.janela} - {new Date(v.data_viagem).toLocaleDateString('pt-BR')} - {v.numero_solicitacao} {v.descricao ? `- ${v.descricao}` : ''}
                    </option>
                  ))}
              </select>
            </div>

            <div style={{marginBottom: 20, position: 'relative'}} className="dropdown-motorista">
              <label style={{display: 'block', marginBottom: 8, fontWeight: 'bold', color: '#1e293b'}}>
                👤 Motorista: <span style={{color: '#dc2626'}}>*</span>
              </label>
              <div
                onClick={() => setDropdownMotoristaAberto(!dropdownMotoristaAberto)}
                style={{
                  width: '100%',
                  padding: 10,
                  borderRadius: 6,
                  border: '1px solid #cbd5e1',
                  fontSize: '1em',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <span style={{color: motoristaSelecionado ? '#1e293b' : '#94a3b8'}}>
                  {motoristaSelecionado 
                    ? motoristas.find(m => m.id === motoristaSelecionado)?.nome 
                    : 'Selecione um motorista'}
                </span>
                <span>▼</span>
              </div>
              {dropdownMotoristaAberto && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: 'white',
                  border: '1px solid #cbd5e1',
                  borderRadius: 6,
                  marginTop: 4,
                  maxHeight: 300,
                  overflow: 'hidden',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                  zIndex: 1000
                }}>
                  <div style={{padding: 8, borderBottom: '1px solid #e2e8f0'}}>
                    <input
                      type="text"
                      placeholder="🔍 Pesquisar motorista..."
                      value={filtroMotorista}
                      onChange={e => setFiltroMotorista(e.target.value)}
                      onClick={e => e.stopPropagation()}
                      style={{
                        width: '100%',
                        padding: 8,
                        borderRadius: 4,
                        border: '1px solid #cbd5e1',
                        fontSize: '0.95em'
                      }}
                    />
                  </div>
                  <div style={{maxHeight: 240, overflowY: 'auto'}}>
                    {motoristas
                      .filter(m => m.nome.toLowerCase().includes(filtroMotorista.toLowerCase()))
                      .map((motorista) => (
                        <div
                          key={motorista.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            setMotoristaSelecionado(motorista.id);
                            setDropdownMotoristaAberto(false);
                            setFiltroMotorista('');
                          }}
                          style={{
                            padding: '10px 12px',
                            cursor: 'pointer',
                            backgroundColor: motoristaSelecionado === motorista.id ? '#f1f5f9' : 'white',
                            borderBottom: '1px solid #f1f5f9'
                          }}
                          onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f8fafc'}
                          onMouseLeave={e => e.currentTarget.style.backgroundColor = motoristaSelecionado === motorista.id ? '#f1f5f9' : 'white'}
                        >
                          {motorista.nome}
                        </div>
                      ))
                    }
                    {motoristas.filter(m => m.nome.toLowerCase().includes(filtroMotorista.toLowerCase())).length === 0 && (
                      <div style={{padding: 12, textAlign: 'center', color: '#94a3b8'}}>
                        Nenhum motorista encontrado
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div style={{marginBottom: 20, position: 'relative'}} className="dropdown-carreta">
              <label style={{display: 'block', marginBottom: 8, fontWeight: 'bold', color: '#1e293b'}}>
                🚛 Carreta: <span style={{color: '#dc2626'}}>*</span>
              </label>
              <div
                onClick={() => setDropdownCarretaAberto(!dropdownCarretaAberto)}
                style={{
                  width: '100%',
                  padding: 10,
                  borderRadius: 6,
                  border: '1px solid #cbd5e1',
                  fontSize: '1em',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <span style={{color: carretaSelecionada ? '#1e293b' : '#94a3b8'}}>
                  {carretaSelecionada 
                    ? `${carretaSelecionada} - ${carretas.find(c => c.placa === carretaSelecionada)?.tipo}`
                    : 'Selecione uma carreta'}
                </span>
                <span>▼</span>
              </div>
              {dropdownCarretaAberto && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: 'white',
                  border: '1px solid #cbd5e1',
                  borderRadius: 6,
                  marginTop: 4,
                  maxHeight: 300,
                  overflow: 'hidden',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                  zIndex: 1000
                }}>
                  <div style={{padding: 8, borderBottom: '1px solid #e2e8f0'}}>
                    <input
                      type="text"
                      placeholder="🔍 Pesquisar carreta..."
                      value={filtroCarreta}
                      onChange={e => setFiltroCarreta(e.target.value)}
                      onClick={e => e.stopPropagation()}
                      style={{
                        width: '100%',
                        padding: 8,
                        borderRadius: 4,
                        border: '1px solid #cbd5e1',
                        fontSize: '0.95em'
                      }}
                    />
                  </div>
                  <div style={{maxHeight: 240, overflowY: 'auto'}}>
                    {carretas
                      .filter(c => (c.placa + ' ' + c.tipo).toLowerCase().includes(filtroCarreta.toLowerCase()))
                      .map((carreta) => (
                        <div
                          key={carreta.placa}
                          onClick={(e) => {
                            e.stopPropagation();
                            setCarretaSelecionada(carreta.placa);
                            setDropdownCarretaAberto(false);
                            setFiltroCarreta('');
                          }}
                          style={{
                            padding: '10px 12px',
                            cursor: 'pointer',
                            backgroundColor: carretaSelecionada === carreta.placa ? '#f1f5f9' : 'white',
                            borderBottom: '1px solid #f1f5f9'
                          }}
                          onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f8fafc'}
                          onMouseLeave={e => e.currentTarget.style.backgroundColor = carretaSelecionada === carreta.placa ? '#f1f5f9' : 'white'}
                        >
                          <div style={{fontWeight: 'bold', color: '#1e293b'}}>{carreta.placa}</div>
                          <div style={{fontSize: '0.85em', color: '#64748b'}}>{carreta.tipo}</div>
                        </div>
                      ))
                    }
                    {carretas.filter(c => (c.placa + ' ' + c.tipo).toLowerCase().includes(filtroCarreta.toLowerCase())).length === 0 && (
                      <div style={{padding: 12, textAlign: 'center', color: '#94a3b8'}}>
                        Nenhuma carreta encontrada
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div style={{display: 'flex', gap: 10, justifyContent: 'flex-end'}}>
              <button
                onClick={() => setShowProgramarModal(false)}
                style={{
                  padding: '10px 20px',
                  background: '#e2e8f0',
                  color: '#475569',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                Cancelar
              </button>
              <button
                onClick={salvarProgramacao}
                style={{
                  padding: '10px 20px',
                  background: 'linear-gradient(135deg, #ec4899 0%, #be185d 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                ✅ Salvar Programação
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Gerenciamento de Viagens */}
      {showViagensModal && (
        <div className="modal-overlay" onClick={() => setShowViagensModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{maxWidth: 700}}>
            <h2 style={{margin: 0, marginBottom: 20, color: '#1e293b', textAlign: 'center'}}>
              🚚 Gerenciar Viagens
            </h2>

            {/* Formulário de nova viagem */}
            <div style={{
              background: '#f8fafc',
              padding: 20,
              borderRadius: 8,
              marginBottom: 20,
              border: '1px solid #e2e8f0'
            }}>
              <h3 style={{margin: 0, marginBottom: 15, color: '#1e293b', fontSize: '1.1em'}}>
                {viagemEditando ? '✏️ Editar Viagem' : '➕ Nova Viagem'}
              </h3>
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr 1.5fr', gap: 10, marginBottom: 10}}>
                <div>
                  <label style={{display: 'block', marginBottom: 5, fontWeight: 'bold', fontSize: '0.9em', color: '#64748b'}}>
                    Janela: *
                  </label>
                  <input
                    type="time"
                    value={novaJanela}
                    onChange={(e) => setNovaJanela(e.target.value)}
                    style={{
                      width: '100%',
                      padding: 8,
                      borderRadius: 6,
                      border: '1px solid #cbd5e1',
                      fontSize: '1em'
                    }}
                  />
                </div>
                <div>
                  <label style={{display: 'block', marginBottom: 5, fontWeight: 'bold', fontSize: '0.9em', color: '#64748b'}}>
                    Data: *
                  </label>
                  <input
                    type="date"
                    value={novaDataViagem}
                    onChange={(e) => setNovaDataViagem(e.target.value)}
                    style={{
                      width: '100%',
                      padding: 8,
                      borderRadius: 6,
                      border: '1px solid #cbd5e1',
                      fontSize: '1em'
                    }}
                  />
                </div>
                <div>
                  <label style={{display: 'block', marginBottom: 5, fontWeight: 'bold', fontSize: '0.9em', color: '#64748b'}}>
                    Nº Solicitação: *
                  </label>
                  <input
                    type="text"
                    placeholder="Ex: 260320001"
                    value={novoNumeroSolicitacao}
                    onChange={(e) => setNovoNumeroSolicitacao(e.target.value)}
                    style={{
                      width: '100%',
                      padding: 8,
                      borderRadius: 6,
                      border: '1px solid #cbd5e1',
                      fontSize: '1em'
                    }}
                  />
                </div>
              </div>
              <div style={{display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, alignItems: 'end'}}>
                <div>
                  <label style={{display: 'block', marginBottom: 5, fontWeight: 'bold', fontSize: '0.9em', color: '#64748b'}}>
                    Descrição (opcional):
                  </label>
                  <input
                    type="text"
                    placeholder="Ex: Viagem para São Paulo"
                    value={novaDescricao}
                    onChange={(e) => setNovaDescricao(e.target.value)}
                    style={{
                      width: '100%',
                      padding: 8,
                      borderRadius: 6,
                      border: '1px solid #cbd5e1',
                      fontSize: '1em'
                    }}
                  />
                </div>
                <div style={{display: 'flex', gap: 8}}>
                  {viagemEditando && (
                    <button
                      onClick={() => {
                        setNovaJanela('');
                        setNovaDataViagem('');
                        setNovoNumeroSolicitacao('');
                        setNovaDescricao('');
                        setViagemEditando(null);
                      }}
                      style={{
                        padding: '8px 12px',
                        background: '#64748b',
                        color: 'white',
                        border: 'none',
                        borderRadius: 6,
                        cursor: 'pointer',
                        fontSize: '0.9em'
                      }}
                    >
                      Cancelar
                    </button>
                  )}
                  <button
                    onClick={salvarViagem}
                    style={{
                      padding: '8px 16px',
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: 6,
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      fontSize: '0.9em'
                    }}
                  >
                    {viagemEditando ? '💾 Atualizar' : '➕ Adicionar'}
                  </button>
                </div>
              </div>
            </div>

            {/* Lista de viagens */}
            <div style={{maxHeight: 400, overflowY: 'auto'}}>
              <h3 style={{margin: 0, marginBottom: 15, color: '#1e293b', fontSize: '1.1em'}}>
                📋 Viagens Cadastradas ({viagens.length})
              </h3>
              {viagens.length === 0 ? (
                <div style={{
                  padding: 40,
                  textAlign: 'center',
                  background: '#f8fafc',
                  borderRadius: 8,
                  border: '2px dashed #cbd5e1'
                }}>
                  <div style={{fontSize: '2em', marginBottom: 10}}>🚚</div>
                  <p style={{color: '#64748b', margin: 0}}>Nenhuma viagem cadastrada</p>
                </div>
              ) : (
                <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
                  {viagens.map((viagem) => (
                    <div
                      key={viagem.cod_horario}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: 15,
                        background: 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: 8,
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)'}
                      onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
                    >
                      <div style={{flex: 1}}>
                        <div style={{display: 'flex', gap: 15, marginBottom: 8}}>
                          <div style={{fontSize: '1.2em', fontWeight: 'bold', color: '#1e293b'}}>
                            🕐 {viagem.janela}
                          </div>
                          <div style={{fontSize: '1.1em', fontWeight: '600', color: '#3b82f6'}}>
                            📅 {new Date(viagem.data_viagem).toLocaleDateString('pt-BR')}
                          </div>
                          <div style={{fontSize: '1.1em', fontWeight: '600', color: '#059669'}}>
                            📋 {viagem.numero_solicitacao}
                          </div>
                        </div>
                        {viagem.descricao && (
                          <div style={{fontSize: '0.9em', color: '#64748b', marginTop: 4}}>
                            {viagem.descricao}
                          </div>
                        )}
                      </div>
                      <div style={{display: 'flex', gap: 8}}>
                        <button
                          onClick={() => editarViagem(viagem)}
                          style={{
                            padding: '6px 12px',
                            background: '#f59e0b',
                            color: 'white',
                            border: 'none',
                            borderRadius: 6,
                            cursor: 'pointer',
                            fontSize: '0.85em',
                            fontWeight: 'bold'
                          }}
                        >
                          ✏️ Editar
                        </button>
                        <button
                          onClick={() => deletarViagem(viagem.cod_horario)}
                          style={{
                            padding: '6px 12px',
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: 6,
                            cursor: 'pointer',
                            fontSize: '0.85em',
                            fontWeight: 'bold'
                          }}
                        >
                          🗑️ Excluir
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{marginTop: 20, textAlign: 'center'}}>
              <button
                onClick={() => {
                  setShowViagensModal(false);
                  setNovaJanela('');
                  setNovaDataViagem('');
                  setNovoNumeroSolicitacao('');
                  setNovaDescricao('');
                  setViagemEditando(null);
                }}
                style={{
                  padding: '10px 30px',
                  background: '#64748b',
                  color: 'white',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default DiarioVeiculosModule;